import { Delete, Folder, KeyboardArrowDown, Sell } from '@mui/icons-material';
import { Divider, ListItemIcon, ListItemText, Menu } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteDesign } from '~/api/designs';
import { BulkActionButton, BulkActionMenuItem } from '~/components/bulk-actions';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { focusableDisabledItems } from '~/components/menu-item';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useBulkActionIds } from '~/hooks/unique-id';

export type BulkActionsProps = {
  selectedIds: GridRowSelectionModel;
  onMoveDesign: () => void;
  onTagDesign: () => void;
  canDestroySelectedDesigns?: boolean;
};

export const BulkActions = ({
  selectedIds,
  onMoveDesign,
  onTagDesign,
  canDestroySelectedDesigns,
}: BulkActionsProps) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const { buttonId, menuId } = useBulkActionIds();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const [deleteDesign] = useDeleteDesign();

  if (!selectedIds.length) return null;

  return (
    <>
      <BulkActionButton
        ref={buttonRef}
        disableRipple
        id={buttonId}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={() => setOpen(true)}
      >
        {t('common.buttons.bulk_actions')}
        <KeyboardArrowDown />
      </BulkActionButton>
      <Menu
        id={menuId}
        anchorEl={buttonRef.current}
        open={open}
        onClose={() => setOpen(false)}
        MenuListProps={{
          ...focusableDisabledItems,
          'aria-labelledby': buttonId,
        }}
      >
        <BulkActionMenuItem
          onClick={() => {
            onMoveDesign();
            setOpen(false);
          }}
        >
          <ListItemIcon>
            <Folder fontSize="small" />
          </ListItemIcon>
          {t('pages.studio.list.bulk_actions.move_to_folder')}
        </BulkActionMenuItem>
        <BulkActionMenuItem
          onClick={() => {
            onTagDesign();
            setOpen(false);
          }}
        >
          <ListItemIcon>
            <Sell fontSize="small" />
          </ListItemIcon>
          {t('common.tags.add_tag')}
        </BulkActionMenuItem>
        <Divider />
        <BulkActionMenuItem
          disabled={!canDestroySelectedDesigns}
          onClick={async () => {
            if (!canDestroySelectedDesigns) return;
            setOpen(false);
            if (!(await confirmDelete())) return;
            await deleteDesign({
              variables: {
                designIds: selectedIds.map((x) => +x),
              },
            });
          }}
          tooltip={
            !canDestroySelectedDesigns && t('pages.studio.list.bulk_actions.cannot_delete_tooltip')
          }
        >
          <ListItemIcon>
            <Delete color="error" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: 'error' }}>
            {t('common.actions.delete')}
          </ListItemText>
        </BulkActionMenuItem>
      </Menu>

      <ConfirmDialog
        {...confirmDeleteProps}
        title={t('pages.studio.dialogs.delete_design.title')}
        prompt={t('pages.studio.dialogs.delete_design.prompt_published')}
        confirm={t('common.dialogs.delete')}
        deleteConfirm
      />
    </>
  );
};
